import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Language,
  ChevronRight,
  InsertLinkOutlined,
  CheckCircleOutline,
  PublicOutlined,
  PaidOutlined,
  TrendingUp,
  TrendingDown,
  AssignmentOutlined,
  MonitorHeartOutlined,
} from '@mui/icons-material';
import { projectService } from '../services/projectService';
import { guestBlogPlacementService } from '../services/guestBlogPlacementService';
import { useProjectWorkflow } from '../contexts/ProjectWorkflowContext';
import LoadingSpinner from '../components/common/LoadingSpinner';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const COMPETITOR_PREVIEW_COUNT = 5;

function urlHref(url: string): string {
  const raw = (url || '').trim();
  if (!raw) return '#';
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

function urlDisplayLabel(url: string, maxLen = 48): string {
  let label = (url || '').trim().replace(/^https?:\/\//i, '').replace(/^www\./i, '');
  if (!label) return url || '';
  if (label.length <= maxLen) return label;
  const head = Math.ceil((maxLen - 1) * 0.55);
  const tail = maxLen - head - 1;
  return `${label.slice(0, head)}…${label.slice(-tail)}`;
}

function competitorFirstLetter(url: string): string {
  const label = urlDisplayLabel(url, 64);
  const ch = label.charAt(0).toUpperCase();
  return /[A-Z0-9]/i.test(ch) ? ch : 'C';
}

const COMPETITOR_AVATAR_COLORS = ['#2563EB', '#9333EA', '#16A34A', '#94A3B8', '#F59E0B', '#EC4899', '#64748B'];

const externalLinkStyle: React.CSSProperties = {
  color: 'var(--accent)',
  textDecoration: 'none',
  fontWeight: 500,
};

const ProjectOverviewPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selectedProject, goToStep } = useProjectWorkflow();
  const projectId = searchParams.get('projectId') || selectedProject?.id;
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [showAllCompetitorUrls, setShowAllCompetitorUrls] = useState(false);

  useEffect(() => {
    setShowAllCompetitorUrls(false);
  }, [projectId]);

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['project-overview', projectId],
    queryFn: () => projectService.getProject(projectId!),
    enabled: Boolean(projectId),
  });

  const { data: placementsData, isLoading: placementsLoading } = useQuery({
    queryKey: ['project-overview-placements', projectId, month, year],
    queryFn: () =>
      guestBlogPlacementService.getPlacements({
        projectId: projectId!,
        month,
        year,
        status: 'COMPLETED',
        limit: 100,
        page: 0,
      }),
    enabled: Boolean(projectId),
  });

  const completedWithLiveUrl = useMemo(() => {
    const rows = placementsData?.data || [];
    return rows
      .filter((pl) => pl.status === 'COMPLETED' && Boolean(pl.liveUrl?.trim()))
      .sort((a, b) => {
        const aTime = new Date(a.updatedAt || a.orderDate || a.createdAt).getTime();
        const bTime = new Date(b.updatedAt || b.orderDate || b.createdAt).getTime();
        return bTime - aTime;
      });
  }, [placementsData]);

  const summaryRows = completedWithLiveUrl.slice(0, 5);
  const monthTotal = useMemo(
    () => completedWithLiveUrl.reduce((t, p) => t + (Number(p.price) || 0), 0),
    [completedWithLiveUrl]
  );

  // Project-wide placements (all months) used to drive the overview stat boxes.
  const { data: allPlacementsData } = useQuery({
    queryKey: ['project-overview-all-placements', projectId],
    queryFn: () =>
      guestBlogPlacementService.getPlacements({
        projectId: projectId!,
        limit: 1000,
        page: 0,
      }),
    enabled: Boolean(projectId),
  });

  const stats = useMemo(() => {
    const all = allPlacementsData?.data || [];
    const total = all.length;
    const completed = all.filter((pl) => pl.status === 'COMPLETED').length;
    const live = all.filter(
      (pl) => pl.status === 'LIVE' || pl.status === 'COMPLETED' || !!(pl.liveUrl && pl.liveUrl.trim())
    ).length;
    const totalSpent = all.reduce((t, pl) => t + (Number(pl.price) || 0), 0);

    const today = new Date();
    const curM = today.getMonth();
    const curY = today.getFullYear();
    const prev = new Date(curY, curM - 1, 1);
    const prevM = prev.getMonth();
    const prevY = prev.getFullYear();
    const inMonth = (d: string | undefined, m: number, y: number) => {
      if (!d) return false;
      const dt = new Date(d);
      return dt.getMonth() === m && dt.getFullYear() === y;
    };
    const thisMonthCount = all.filter((pl) => inMonth(pl.orderDate, curM, curY)).length;
    const lastMonthCount = all.filter((pl) => inMonth(pl.orderDate, prevM, prevY)).length;
    let momPct: number;
    if (lastMonthCount === 0) momPct = thisMonthCount > 0 ? 100 : 0;
    else momPct = Math.round(((thisMonthCount - lastMonthCount) / lastMonthCount) * 100);

    const completionPct = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { total, completed, live, totalSpent, momPct, completionPct };
  }, [allPlacementsData]);

  const monthlyStats = useMemo(() => {
    const all = allPlacementsData?.data || [];
    const monthPlacements = all.filter((pl) => {
      if (!pl.orderDate) return false;
      const dt = new Date(pl.orderDate);
      return dt.getMonth() + 1 === month && dt.getFullYear() === year;
    });
    const monthSpent = monthPlacements.reduce((t, pl) => t + (Number(pl.price) || 0), 0);
    const monthCompleted = monthPlacements.filter((pl) => pl.status === 'COMPLETED').length;
    const budget = project?.budget != null ? Number(project.budget) : null;
    const remaining = budget != null && budget > 0 ? budget - monthSpent : null;
    const usedPct =
      budget != null && budget > 0 ? Math.min(100, Math.round((monthSpent / budget) * 100)) : null;

    return {
      placements: monthPlacements.length,
      completed: monthCompleted,
      spent: monthSpent,
      budget,
      remaining,
      usedPct,
    };
  }, [allPlacementsData, month, year, project?.budget]);

  if (!projectId) {
    return <div style={{ padding: 24, color: 'var(--text-muted)' }}>Select a project from the sidebar.</div>;
  }

  if (projectLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <LoadingSpinner />
      </div>
    );
  }

  const p = project!;
  const color = selectedProject?.color || '#2563eb';
  const projectUrl = (p.websiteUrl || '').trim();
  const clientId = p.clientId || p.client?.id || selectedProject?.clientId || '';
  const competitorUrls = (p.competitor_links || [])
    .map((link) => (link || '').trim())
    .filter(Boolean);
  const visibleCompetitorUrls = showAllCompetitorUrls
    ? competitorUrls
    : competitorUrls.slice(-COMPETITOR_PREVIEW_COUNT);
  const hasMoreCompetitorUrls = competitorUrls.length > COMPETITOR_PREVIEW_COUNT;

  const goToCompletedOrderStatus = () => {
    const clientId = p.clientId || p.client?.id || selectedProject?.clientId || '';
    if (!projectId || !clientId) {
      goToStep('status');
      return;
    }
    const params = new URLSearchParams({
      projectId,
      clientId,
      status: 'COMPLETED',
      month: String(month),
      year: String(year),
    });
    navigate(`/guest-blog-placements?${params.toString()}`);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        {/* <span style={{ width: 10, height: 10, borderRadius: '50%', background: color }} /> */}
        <div>
          <div style={{ fontSize: 20, fontWeight: 600, lineHeight: 1.25 }}>
            {projectUrl ? (
              <a
                href={urlHref(projectUrl)}
                target="_blank"
                rel="noopener noreferrer"
                title={projectUrl}
                style={externalLinkStyle}
              >
                {p.projectName}
              </a>
            ) : (
              p.projectName
            )}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            {p.client?.name || selectedProject?.clientName || '—'} · Active
          </div>
        </div>
        <button
          type="button"
          className="wf-btn-create"
          style={{ marginLeft: 'auto' }}
          onClick={() => goToStep('sites')}
        >
          <Language sx={{ fontSize: 15 }} />
          Explore Sites
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        {[
          {
            label: 'Total Orders',
            value: stats.total,
            color: 'var(--accent)',
            icon: <InsertLinkOutlined sx={{ fontSize: 16 }} />,
            sub: (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: stats.momPct >= 0 ? 'var(--green)' : 'var(--red)' }}>
                {stats.momPct >= 0 ? <TrendingUp sx={{ fontSize: 13 }} /> : <TrendingDown sx={{ fontSize: 13 }} />}
                {stats.momPct >= 0 ? '+' : ''}{stats.momPct}% from last month
              </span>
            ),
          },
          {
            label: 'Completed',
            value: stats.completed,
            color: 'var(--green)',
            icon: <CheckCircleOutline sx={{ fontSize: 16 }} />,
            sub: `${stats.completionPct}% completion`,
          },
          {
            label: 'Live Links',
            value: stats.live,
            color: 'var(--amber)',
            icon: <PublicOutlined sx={{ fontSize: 16 }} />,
            sub: 'Monitored 24/7',
          },
          {
            label: 'Total Spent',
            value: `$${stats.totalSpent.toLocaleString()}`,
            color: 'var(--purple)',
            icon: <PaidOutlined sx={{ fontSize: 16 }} />,
            sub: 'All-time total',
          },
        ].map((card) => (
          <div
            key={card.label}
            style={{
              background: 'var(--surface)',
              border: '0.5px solid var(--border)',
              borderRadius: 12,
              padding: '16px 16px 14px',
            }}
          >
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: card.color,
                background: 'color-mix(in srgb, currentColor 14%, transparent)',
                marginBottom: 10,
              }}
            >
              {card.icon}
            </div>
            <div style={{ fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5 }}>
              {card.label}
            </div>
            <div style={{ fontSize: 26, fontWeight: 600, color: card.color, lineHeight: 1, marginBottom: 6 }}>
              {card.value}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{card.sub}</div>
          </div>
        ))}
      </div>

      <div
        style={{
          background: 'var(--surface)',
          border: '0.5px solid var(--border)',
          borderRadius: 12,
          padding: '16px 18px',
          marginBottom: 16,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 14,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Monthly stats</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              {MONTHS[month - 1]} {year}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Month:</span>
            <select
              value={`${year}-${month}`}
              onChange={(e) => {
                const [y, m] = e.target.value.split('-').map(Number);
                setYear(y);
                setMonth(m);
              }}
              style={{
                background: 'var(--surface2)',
                border: '0.5px solid var(--border)',
                borderRadius: 7,
                padding: '5px 10px',
                fontSize: 12,
              }}
            >
              {MONTHS.map((name, i) => (
                <option key={name} value={`${year}-${i + 1}`}>
                  {name} {year}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          {[
            {
              label: 'Monthly Budget',
              value:
                monthlyStats.budget != null && monthlyStats.budget > 0
                  ? `$${monthlyStats.budget.toLocaleString()}`
                  : 'Not set',
              sub: 'Project budget for the month',
            },
            {
              label: 'Monthly Used',
              value: `$${monthlyStats.spent.toLocaleString()}`,
              sub:
                monthlyStats.usedPct != null
                  ? `${monthlyStats.usedPct}% of budget used`
                  : 'Spent this month',
            },
            {
              label: 'Remaining',
              value:
                monthlyStats.remaining != null
                  ? `$${Math.max(0, monthlyStats.remaining).toLocaleString()}`
                  : '—',
              sub:
                monthlyStats.budget != null && monthlyStats.budget > 0
                  ? 'Budget left this month'
                  : 'No budget set',
            },
            {
              label: 'Orders',
              value: String(monthlyStats.placements),
              sub: `${monthlyStats.completed} completed`,
            },
          ].map((card) => (
            <div
              key={card.label}
              style={{
                background: 'var(--surface2)',
                border: '0.5px solid var(--border)',
                borderRadius: 10,
                padding: '14px 14px 12px',
              }}
            >
              <div
                style={{
                  fontSize: 10.5,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  marginBottom: 6,
                }}
              >
                {card.label}
              </div>
              <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1, marginBottom: 6 }}>
                {card.value}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{card.sub}</div>
            </div>
          ))}
        </div>
      </div>

      <div
        style={{
          background: 'var(--surface)',
          border: '0.5px solid var(--border)',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '13px 18px',
            borderBottom: '0.5px solid var(--border)',
          }}
        >
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Order summary</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              {completedWithLiveUrl.length} completed with live URL ·{' '}
              <span style={{ color: 'var(--accent)', fontWeight: 600 }}>${monthTotal} spent</span>
              {' · '}
              {MONTHS[month - 1]} {year}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 11, cursor: 'pointer' }}
              onClick={goToCompletedOrderStatus}
            >
              See more <ChevronRight sx={{ fontSize: 12, verticalAlign: 'middle' }} />
            </button>
          </div>
        </div>
        {placementsLoading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <LoadingSpinner />
          </div>
        ) : summaryRows.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
            No completed orders with live URLs for this month.
          </div>
        ) : (
          summaryRows.map((pl) => (
            <div
              key={pl.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1.5fr 130px 80px',
                padding: '11px 18px',
                borderBottom: '0.5px solid var(--border)',
                fontSize: 12,
                alignItems: 'center',
              }}
            >
              <a
                href={pl.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontWeight: 500,
                  color: 'var(--accent)',
                  textDecoration: 'none',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={pl.liveUrl}
              >
                {pl.liveUrl}
              </a>
              <span>{guestBlogPlacementService.getStatusLabel(pl.status)}</span>
              <span style={{ fontWeight: 600 }}>${pl.price ?? 0}</span>
            </div>
          ))
        )}
      </div>

      {competitorUrls.length > 0 && (
        <div
          style={{
            marginTop: 16,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: 24,
            boxShadow: 'var(--shadow)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 20,
            }}
          >
            <div>
              <h2
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  margin: '0 0 4px 0',
                }}
              >
                Competitor URLs
              </h2>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>
                Competitor websites added to this project
              </p>
            </div>
            {hasMoreCompetitorUrls && (
              <button
                type="button"
                onClick={() => setShowAllCompetitorUrls((prev) => !prev)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: 0,
                  flexShrink: 0,
                }}
              >
                {showAllCompetitorUrls ? 'Show less' : `Show all (${competitorUrls.length})`}
              </button>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {visibleCompetitorUrls.map((url, index) => (
              <a
                key={`${url}-${index}`}
                href={urlHref(url)}
                target="_blank"
                rel="noopener noreferrer"
                title={url}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: 12,
                  borderRadius: 'var(--radius)',
                  background: 'var(--surface2)',
                  textDecoration: 'none',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--surface3)';
                  e.currentTarget.style.transform = 'translateX(4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--surface2)';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    background: COMPETITOR_AVATAR_COLORS[index % COMPETITOR_AVATAR_COLORS.length],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 16,
                    fontWeight: 600,
                    color: 'white',
                    flexShrink: 0,
                  }}
                >
                  {competitorFirstLetter(url)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: 'var(--text-primary)',
                      marginBottom: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {urlDisplayLabel(url, 56)}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Open competitor site</div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexShrink: 0,
                    color: 'var(--text-secondary)',
                  }}
                >
                  <ChevronRight sx={{ fontSize: 18 }} />
                </div>
              </a>
            ))}
          </div>

          {!showAllCompetitorUrls && hasMoreCompetitorUrls && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 14 }}>
              Showing last {COMPETITOR_PREVIEW_COUNT} of {competitorUrls.length} competitor URLs
            </div>
          )}
        </div>
      )}

      <div
        style={{
          marginTop: 16,
          background: 'var(--surface)',
          border: '0.5px solid var(--border)',
          borderRadius: 12,
          padding: 16,
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Quick actions</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          {[
            { label: 'Explore more sites', icon: <Language sx={{ fontSize: 16 }} />, onClick: () => goToStep('sites') },
            { label: 'View saved orders', icon: <AssignmentOutlined sx={{ fontSize: 16 }} />, onClick: () => goToStep('plan') },
            { label: 'Order status', icon: <MonitorHeartOutlined sx={{ fontSize: 16 }} />, onClick: () => goToStep('status') },
          ].map((action) => (
            <button
              key={action.label}
              type="button"
              onClick={action.onClick}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                width: '100%',
                textAlign: 'center',
                padding: '11px 14px',
                borderRadius: 9,
                border: 'none',
                background: 'var(--accent)',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.filter = 'brightness(0.93)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.filter = 'none';
              }}
            >
              {action.icon}
              {action.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProjectOverviewPage;
